import { formatTime } from '@/lib/format';

export interface ComparisonSummaryBannerProps {
  titleA: string;
  badgeA?: string;
  subtitleA: string;
  timeLabelA: string;
  timeMsA?: number;
  titleB: string;
  badgeB?: string;
  subtitleB: string;
  timeLabelB: string;
  timeMsB?: number;
}

export function ComparisonSummaryBanner({
  titleA,
  badgeA,
  subtitleA,
  timeLabelA,
  timeMsA,
  titleB,
  badgeB,
  subtitleB,
  timeLabelB,
  timeMsB,
}: ComparisonSummaryBannerProps) {
  const diffMs =
    timeMsA && timeMsB && timeMsA > 0 && timeMsB > 0
      ? timeMsB - timeMsA
      : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Side A Summary Card (Red #ef4444) */}
      <div className="rounded-xl px-4 py-3 bg-red-500/15 border-2 border-red-400/60 border-l-8 border-l-red-500 shadow-lg shadow-red-500/10 flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-3 h-3 rounded-full bg-red-500 shadow-sm shadow-red-500 shrink-0" />
            <span className="text-sm font-bold text-red-50 truncate">
              {titleA}
            </span>
            {badgeA && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/25 text-red-200 border border-red-400/40 font-semibold shrink-0">
                {badgeA}
              </span>
            )}
          </div>
          <span className="text-xs text-red-200/80 font-mono pl-5 truncate">
            {subtitleA}
          </span>
        </div>

        <div className="px-3.5 py-2 rounded-lg bg-slate-950/55 border border-red-400/35 shrink-0 text-right">
          <div className="text-[10px] uppercase tracking-wider text-red-300/85 font-semibold mb-0.5">
            {timeLabelA}
          </div>
          <div className="text-sm font-bold font-mono text-white">
            {timeMsA && timeMsA > 0 ? formatTime(timeMsA) : '—'}
          </div>
        </div>
      </div>

      {/* Side B Summary Card (Blue #3b82f6) */}
      <div className="rounded-xl px-4 py-3 bg-blue-500/15 border-2 border-blue-400/60 border-l-8 border-l-blue-500 shadow-lg shadow-blue-500/10 flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-3 h-3 rounded-full bg-blue-500 shadow-sm shadow-blue-500 shrink-0" />
            <span className="text-sm font-bold text-blue-50 truncate">
              {titleB}
            </span>
            {badgeB && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/25 text-blue-200 border border-blue-400/40 font-semibold shrink-0">
                {badgeB}
              </span>
            )}
          </div>
          <span className="text-xs text-blue-200/80 font-mono pl-5 truncate">
            {subtitleB}
          </span>
        </div>

        <div className="px-3.5 py-2 rounded-lg bg-slate-950/55 border border-blue-400/35 shrink-0 text-right">
          <div className="text-[10px] uppercase tracking-wider text-blue-300/85 font-semibold mb-0.5">
            {timeLabelB}
          </div>
          <div className="flex items-baseline justify-end gap-1.5">
            <span className="text-sm font-bold font-mono text-white">
              {timeMsB && timeMsB > 0 ? formatTime(timeMsB) : '—'}
            </span>
            {diffMs !== 0 && (
              <span
                className="text-[11px] font-mono font-semibold"
                style={{ color: diffMs < 0 ? '#60a5fa' : '#f87171' }}
              >
                {diffMs > 0 ? '+' : ''}
                {(diffMs / 1000).toFixed(2)}s
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
